import { isNil } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Image } from 'react-native';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBImage } from '../../image/index';
import { useCreateQrCode } from '../hooks/useCreateQrCode';

//#region Constants

/**
 * The expected (i.e. known) aspect ratio of the QR code image.
 *
 * This is the natural aspect ratio of the custom QR code design in our QR code generator package.
 */
const EXPECTED_ASPECT_RATIO = 4 / 3;

//#endregion

//#region Public Interface

interface Props {
  /**
   * The URL to encode in the QR code.
   */
  url: string;

  /**
   * Optional size object specifying the width and/or height of the image.
   *
   * - If both width and height are provided, they will be used as-is.
   * - If only one dimension is provided, the other dimension will be calculated
   * based on the image's aspect ratio.
   * - If neither dimension is provided, the image's natural dimensions will be used.
   */
  size?: {
    height?: number;
    width?: number;
  };

  /**
   * Callback function that is called with the URL of the generated QR code image.
   *
   * This URL is suitable for display and for sharing via react-native-share.
   *
   * @param url The URL of the generated QR code image.
   */
  onQrCodeUrl?: (url: string) => void;
}

export function QRCode({ size, url, onQrCodeUrl }: Props) {
  const colors = useContext(ColorThemeContext);
  const placeholderSize = useMemo(() => calculatePlaceholderSize(size), [size]);
  const [imageSize, setImageSize] = useState<{ height: number; width: number } | undefined>();
  const { response, createQrCode } = useCreateQrCode();

  useEffect(() => {
    createQrCode(url);
  }, [url, createQrCode]);

  useEffect(() => {
    if (response?.state === 'success') {
      calculateImageSize(response.data, size).then(setImageSize);
    }
  }, [response, size]);

  useEffect(() => {
    if (response?.state === 'success') {
      onQrCodeUrl?.(response.data);
    }
  }, [onQrCodeUrl, response]);

  switch (response?.state) {
    case undefined:
    case 'loading':
      return <MBLoadingIndicator style={{ ...placeholderSize }} />;
    case 'success':
      return (
        <MBImage style={{ ...(imageSize ?? placeholderSize) }} source={{ uri: response.data }} />
      );
    case 'failure':
      return (
        <MBTextBody2 color={colors.error.foreground}>
          {i18n('errors.somethingWentWrong')}
        </MBTextBody2>
      );
  }
}

//#endregion

//#region Private Interface

function calculateImageSize(
  url: string,
  desiredSize?: { height?: number; width?: number },
): Promise<{ height: number; width: number }> {
  return new Promise(resolve => {
    if (isNil(desiredSize) || isNil(desiredSize.height) || isNil(desiredSize.width)) {
      Image.getSize(url, (width, height) => {
        if (isNil(desiredSize)) {
          resolve({ height, width });
        } else if (!isNil(desiredSize.width)) {
          resolve({ height: (desiredSize.width / width) * height, width: desiredSize.width });
        } else if (!isNil(desiredSize.height)) {
          resolve({ height: desiredSize.height, width: (desiredSize.height / height) * width });
        }
      });
    } else {
      resolve({ height: desiredSize.height, width: desiredSize.width });
    }
  });
}

function calculatePlaceholderSize(desiredSize?: {
  height?: number;
  width?: number;
}): { height: number; width: number } | undefined {
  if (isNil(desiredSize)) {
    return undefined;
  }

  if (!isNil(desiredSize.height) && !isNil(desiredSize.width)) {
    return { height: desiredSize.height, width: desiredSize.width };
  }

  if (!isNil(desiredSize.height)) {
    return { height: desiredSize.height, width: desiredSize.height / EXPECTED_ASPECT_RATIO };
  }

  if (!isNil(desiredSize.width)) {
    return { height: desiredSize.width * EXPECTED_ASPECT_RATIO, width: desiredSize.width };
  }

  return undefined;
}

//#endregion
